import * as RadixPopover from "@radix-ui/react-popover";
import { CalendarBlank, CaretDown, CaretLeft, CaretRight, Check, X } from "@phosphor-icons/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { kfContentIn, twRing, twTransform } from "@/lib/emotionTw";
import { Button } from "@/components/primitives/Button";
import { Select } from "@/components/ui/forms/Select";
import { WarningAlertIcon, warningSurfaceTextStyle } from "@/components/ui/feedback/warningVisual";
import { BfrsPortalScope } from "@/components/ui/providers/BfrsProvider";
import { floatingCollisionPadding } from "@/components/ui/overlays/overlayPositioning";
import {
  CUSTOM_PRESET_ID,
  DAY_HEADS,
  MONTH_NAMES,
  SHORT_MONTH_NAMES,
  addDays,
  coerceDateRangeValue,
  compareDay,
  daysInMonth,
  defaultDateRangePresets,
  defaultFormatDate,
  differenceInCalendarDays,
  getInitialCalendar,
  getPresetLabel,
  hasCompleteRange,
  normalizeRange,
  sameDay,
  shiftMonth,
  startOfDay
} from "./DateRangePicker.utils";
import type { DateRangePickerProps, DateRangePreset, DateRangePresetId, DateRangeValue } from "./DateRangePicker.types";

/* Styles are the exact compiled output of the pre-migration Tailwind
 * utilities, in the original class-list emission order. Two pre-existing
 * dead classes (`bfrs-[scrollbar-width:none]`, `bfrs-[-ms-overflow-style:none]`
 * — never emitted by Tailwind because the prefix breaks arbitrary-property
 * syntax) are intentionally NOT reproduced; only the working
 * `::-webkit-scrollbar` rule carries over. Verified against the frozen
 * baseline. */

const focusRing = (alpha?: string): CSSObject =>
  alpha
    ? twRing("2px", `hsl(var(--bfrs-color-border-focus) / ${alpha})`)
    : {
        ...twRing("2px", "hsl(var(--bfrs-color-border-focus) / var(--tw-ring-opacity, 1))"),
        "--tw-ring-opacity": "1"
      };

const textMuted: CSSObject = {
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
};
const textPrimaryToken: CSSObject = {
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
};
const textSecondaryToken: CSSObject = {
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-secondary) / var(--tw-text-opacity, 1))"
};
const textDisabledToken: CSSObject = {
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-disabled) / var(--tw-text-opacity, 1))"
};
const bgPrimarySoft: CSSObject = {
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))"
};
const bgSurfaceCard: CSSObject = {
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))"
};
const borderBorderToken: CSSObject = {
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))"
};

type CalendarMonthCoordinate = {
  year: number;
  month: number;
};

function compareMonth(a: CalendarMonthCoordinate, b: CalendarMonthCoordinate): number {
  const left = Date.UTC(a.year, a.month, 1);
  const right = Date.UTC(b.year, b.month, 1);
  return left === right ? 0 : left < right ? -1 : 1;
}

function isMonthOutsideBounds(month: CalendarMonthCoordinate, min: CalendarMonthCoordinate, max: CalendarMonthCoordinate): boolean {
  return compareMonth(month, min) < 0 || compareMonth(month, max) > 0;
}

function monthCoordinate(date: Date): CalendarMonthCoordinate {
  return { year: date.getFullYear(), month: date.getMonth() };
}

function getRangeCalendarWindow(from: Date, to: Date, months: 1 | 2): { left: CalendarMonthCoordinate; right: CalendarMonthCoordinate } {
  const left = monthCoordinate(from);

  return {
    left,
    right: months === 2 ? monthCoordinate(to) : left
  };
}

function getVisibleCalendarMonthCount(months: 1 | 2, value: DateRangeValue): 1 | 2 {
  if (months === 1 || !hasCompleteRange(value)) return months;
  return compareMonth(monthCoordinate(value.from), monthCoordinate(value.to)) === 0 ? 1 : 2;
}

/* ---------- calendar slots ---------- */

const CalendarRoot = styled("div", {
  label: "AgenticDateRangePicker-calendar"
})({
  width: "252px",
  borderRadius: "var(--bfrs-radius-xl)",
  borderWidth: "1px",
  ...borderBorderToken,
  ...bgSurfaceCard,
  padding: ".75rem",
  "--tw-shadow": "var(--bfrs-shadow-low)",
  "--tw-shadow-colored": "var(--bfrs-shadow-low)",
  boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)"
});

const CalendarHeader = styled("div", {
  label: "AgenticDateRangePicker-calendarHeader"
})({
  marginBottom: ".5rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between"
});

const CalendarNavButton = styled("button", {
  label: "AgenticDateRangePicker-navButton",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { disableable: boolean } }>(({ ownerState }) => [
  {
    display: "flex",
    width: "1.75rem",
    height: "1.75rem",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "var(--bfrs-radius-md)",
    ...textMuted,
    outline: "2px solid transparent",
    outlineOffset: "2px",
    "&:hover": {
      ...bgPrimarySoft,
      ...textPrimaryToken
    },
    "&:focus-visible": focusRing()
  },
  ownerState.disableable && {
    "&:disabled": {
      pointerEvents: "none",
      opacity: ".3"
    }
  }
]);

const CalendarNavSpacer = styled("div", {
  label: "AgenticDateRangePicker-navSpacer"
})({
  width: "1.75rem",
  height: "1.75rem"
});

const CalendarTitleButton = styled("button", {
  label: "AgenticDateRangePicker-titleButton"
})({
  display: "flex",
  alignItems: "center",
  gap: ".25rem",
  fontSize: ".875rem",
  lineHeight: "1.25rem",
  fontWeight: 700,
  ...textPrimaryToken,
  outline: "2px solid transparent",
  outlineOffset: "2px",
  "&:hover": {
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-primary) / var(--tw-text-opacity, 1))"
  },
  transitionProperty: "color,background-color,border-color,text-decoration-color,fill,stroke",
  transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
  transitionDuration: ".15s"
});

const CalendarTitleCaret = styled(CaretDown, {
  label: "AgenticDateRangePicker-titleCaret",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { open: boolean } }>(({ ownerState }) => [
  {
    ...textMuted,
    transitionProperty: "transform",
    transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
    transitionDuration: ".15s"
  },
  ownerState.open && twTransform({ "--tw-rotate": "180deg" })
]);

const CalendarMonthGrid = styled("div", {
  label: "AgenticDateRangePicker-monthGrid"
})({
  display: "grid",
  gridTemplateColumns: "repeat(3,minmax(0,1fr))",
  gap: ".375rem",
  paddingTop: ".25rem",
  paddingBottom: ".25rem"
});

const CalendarMonthButton = styled("button", {
  label: "AgenticDateRangePicker-monthButton",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { current: boolean; disabled: boolean } }>(({ ownerState }) => [
  {
    borderRadius: "var(--bfrs-radius-lg)",
    paddingLeft: ".25rem",
    paddingRight: ".25rem",
    paddingTop: ".625rem",
    paddingBottom: ".625rem",
    fontSize: "12px",
    fontWeight: 500,
    ...textSecondaryToken,
    outline: "2px solid transparent",
    outlineOffset: "2px",
    transitionProperty:
      "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
    transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
    transitionDuration: ".15s",
    "&:hover": {
      ...bgPrimarySoft,
      ...textPrimaryToken
    },
    "&:focus-visible": focusRing()
  },
  ownerState.current && {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-primary) / var(--tw-bg-opacity, 1))",
    fontWeight: 700,
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-primary-contrast) / var(--tw-text-opacity, 1))",
    "&:hover": {
      "--tw-bg-opacity": "1",
      backgroundColor: "hsl(var(--bfrs-color-primary) / var(--tw-bg-opacity, 1))",
      "--tw-text-opacity": "1",
      color: "hsl(var(--bfrs-color-primary-contrast) / var(--tw-text-opacity, 1))"
    }
  },
  ownerState.disabled && {
    cursor: "not-allowed",
    ...textDisabledToken,
    opacity: ".45",
    "&:hover": {
      backgroundColor: "transparent",
      ...textDisabledToken
    }
  }
]);

const CalendarDayHeads = styled("div", {
  label: "AgenticDateRangePicker-dayHeads"
})({
  marginBottom: ".125rem",
  display: "grid",
  gridTemplateColumns: "repeat(7,minmax(0,1fr))",
  borderBottomWidth: "1px",
  ...borderBorderToken,
  paddingBottom: ".375rem"
});

const CalendarDayHead = styled("div", {
  label: "AgenticDateRangePicker-dayHead"
})({
  textAlign: "center",
  fontSize: ".75rem",
  lineHeight: "1rem",
  fontWeight: 600,
  ...textDisabledToken
});

const CalendarDaysGrid = styled("div", {
  label: "AgenticDateRangePicker-daysGrid"
})({
  display: "grid",
  gridTemplateColumns: "repeat(7,minmax(0,1fr))"
});

const CalendarMonthLabel = styled("div", {
  label: "AgenticDateRangePicker-monthLabel"
})({
  gridColumn: "span 7 / span 7",
  paddingLeft: ".125rem",
  paddingRight: ".125rem",
  paddingTop: ".25rem",
  paddingBottom: ".25rem",
  fontSize: "11px",
  fontWeight: 700,
  letterSpacing: ".12em",
  ...textDisabledToken
});

const CalendarBlankCell = styled("div", {
  label: "AgenticDateRangePicker-blankCell"
})({
  height: "2rem"
});

const CalendarDayCell = styled("div", {
  label: "AgenticDateRangePicker-dayCell"
})({
  position: "relative",
  display: "flex",
  height: "2rem",
  alignItems: "center",
  justifyContent: "center"
});

type BandShape = "start" | "end" | "middle";

const CalendarRangeBand = styled("div", {
  label: "AgenticDateRangePicker-rangeBand",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { shape: BandShape; roundLeft: boolean; roundRight: boolean } }>(({ ownerState }) => [
  {
    pointerEvents: "none",
    position: "absolute",
    top: "2px",
    bottom: "2px",
    ...bgPrimarySoft
  },
  ownerState.shape === "start" && { left: "50%", right: 0 },
  ownerState.shape === "end" && { left: 0, right: "50%" },
  ownerState.shape === "middle" && { left: 0, right: 0 },
  ownerState.shape === "middle" &&
    ownerState.roundLeft && {
      borderTopLeftRadius: "var(--bfrs-radius-lg)",
      borderBottomLeftRadius: "var(--bfrs-radius-lg)"
    },
  ownerState.shape === "middle" &&
    ownerState.roundRight && {
      borderTopRightRadius: "var(--bfrs-radius-lg)",
      borderBottomRightRadius: "var(--bfrs-radius-lg)"
    }
]);

interface DayButtonOwnerState {
  shape: "start" | "end" | "single";
  selected: boolean;
  inRange: boolean;
  disabled: boolean;
}

const CalendarDayButton = styled("button", {
  label: "AgenticDateRangePicker-dayButton",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: DayButtonOwnerState }>(({ ownerState }) => [
  {
    position: "relative",
    zIndex: 10,
    display: "flex",
    width: "1.75rem",
    height: "1.75rem",
    cursor: "pointer",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px",
    outline: "2px solid transparent",
    outlineOffset: "2px",
    transitionProperty:
      "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
    transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
    transitionDuration: ".15s",
    "&:focus-visible": focusRing()
  },
  ownerState.shape === "start"
    ? {
        borderTopLeftRadius: "var(--bfrs-radius-lg)",
        borderBottomLeftRadius: "var(--bfrs-radius-lg)"
      }
    : ownerState.shape === "end"
      ? {
          borderTopRightRadius: "var(--bfrs-radius-lg)",
          borderBottomRightRadius: "var(--bfrs-radius-lg)"
        }
      : { borderRadius: "var(--bfrs-radius-lg)" },
  ownerState.selected && {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-primary) / var(--tw-bg-opacity, 1))",
    fontWeight: 700,
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-primary-contrast) / var(--tw-text-opacity, 1))",
    "--tw-shadow": "var(--bfrs-shadow-low)",
    "--tw-shadow-colored": "var(--bfrs-shadow-low)",
    boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)"
  },
  !ownerState.selected && ownerState.inRange && { fontWeight: 500, ...textPrimaryToken },
  !ownerState.selected &&
    !ownerState.inRange && {
      ...textSecondaryToken,
      "&:hover": {
        ...bgPrimarySoft,
        ...textPrimaryToken
      }
    },
  ownerState.disabled && {
    cursor: "not-allowed",
    ...textDisabledToken,
    opacity: ".4",
    "&:hover": {
      backgroundColor: "transparent",
      ...textDisabledToken
    }
  }
]);

type CalendarMonthProps = {
  year: number;
  month: number;
  from?: Date;
  to?: Date;
  hovered?: Date;
  awaitingTo: boolean;
  isDateDisabled: (date: Date) => boolean;
  onSelectDate: (date: Date) => void;
  onHoverDate: (date?: Date) => void;
  onPrev: () => void;
  onNext: () => void;
  showPrev?: boolean;
  showNext?: boolean;
  minMonth: CalendarMonthCoordinate;
  maxMonth: CalendarMonthCoordinate;
  onNavigateTo?: (year: number, month: number) => void;
  className?: string;
};

function CalendarMonth({
  year,
  month,
  from,
  to,
  hovered,
  awaitingTo,
  isDateDisabled,
  onSelectDate,
  onHoverDate,
  onPrev,
  onNext,
  showPrev = true,
  showNext = true,
  minMonth,
  maxMonth,
  onNavigateTo,
  className
}: CalendarMonthProps) {
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [pickerYear, setPickerYear] = useState(year);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    if (!showMonthPicker) setPickerYear(year);
  }, [year, showMonthPicker]);

  const firstDayOffset = new Date(year, month, 1).getDay();
  const totalDays = daysInMonth(year, month);
  const cells: Array<number | null> = Array(firstDayOffset).fill(null);

  for (let day = 1; day <= totalDays; day += 1) {
    cells.push(day);
  }

  while (cells.length % 7 !== 0) {
    cells.push(null);
  }

  const rangeEnd = to ?? (awaitingTo ? hovered : undefined);
  const range = from && rangeEnd ? normalizeRange(from, rangeEnd) : undefined;

  return (
    <CalendarRoot className={className}>
      <CalendarHeader>
        {showMonthPicker ? (
          <CalendarNavButton
            type="button"
            ownerState={{ disableable: true }}
            onClick={() => setPickerYear((y) => y - 1)}
            disabled={pickerYear <= BASE_YEAR}
            aria-label="Previous year"
          >
            <CaretLeft size={14} weight="bold" />
          </CalendarNavButton>
        ) : showPrev ? (
          <CalendarNavButton type="button" ownerState={{ disableable: false }} onClick={onPrev} aria-label="Previous month">
            <CaretLeft size={14} weight="bold" />
          </CalendarNavButton>
        ) : (
          <CalendarNavSpacer aria-hidden="true" />
        )}
        <CalendarTitleButton
          type="button"
          onClick={() => { setPickerYear(year); setShowMonthPicker((v) => !v); }}
        >
          {showMonthPicker ? pickerYear : `${MONTH_NAMES[month]}, ${year}`}
          <CalendarTitleCaret size={11} weight="bold" ownerState={{ open: showMonthPicker }} />
        </CalendarTitleButton>
        {showMonthPicker ? (
          <CalendarNavButton
            type="button"
            ownerState={{ disableable: true }}
            onClick={() => setPickerYear((y) => y + 1)}
            disabled={pickerYear >= currentYear + 1}
            aria-label="Next year"
          >
            <CaretRight size={14} weight="bold" />
          </CalendarNavButton>
        ) : showNext ? (
          <CalendarNavButton type="button" ownerState={{ disableable: false }} onClick={onNext} aria-label="Next month">
            <CaretRight size={14} weight="bold" />
          </CalendarNavButton>
        ) : (
          <CalendarNavSpacer aria-hidden="true" />
        )}
      </CalendarHeader>

      {showMonthPicker ? (
        <CalendarMonthGrid>
          {SHORT_MONTH_NAMES.map((name, idx) => {
            const isCurrentMonth = idx === month && pickerYear === year;
            const disabled = isMonthOutsideBounds({ year: pickerYear, month: idx }, minMonth, maxMonth);
            return (
              <CalendarMonthButton
                key={idx}
                type="button"
                disabled={disabled}
                ownerState={{ current: isCurrentMonth, disabled }}
                onClick={() => {
                  if (disabled) return;
                  onNavigateTo?.(pickerYear, idx);
                  setShowMonthPicker(false);
                }}
              >
                {name}
              </CalendarMonthButton>
            );
          })}
        </CalendarMonthGrid>
      ) : (
        <>
      <CalendarDayHeads>
        {DAY_HEADS.map((day, index) => (
          <CalendarDayHead key={`${day}-${index}`}>{day}</CalendarDayHead>
        ))}
      </CalendarDayHeads>

      <CalendarDaysGrid>
        <CalendarMonthLabel>{SHORT_MONTH_NAMES[month]}</CalendarMonthLabel>
        {cells.map((day, index) => {
          if (!day) {
            return <CalendarBlankCell key={`blank-${index}`} />;
          }

          const date = new Date(year, month, day);
          const selectedStart = sameDay(date, range?.from ?? from);
          const selectedEnd = sameDay(date, range?.to ?? to);
          const selected = selectedStart || selectedEnd;
          const inRange = Boolean(range && compareDay(date, range.from) > 0 && compareDay(date, range.to) < 0);
          const disabled = isDateDisabled(date);

          // Grey range band. Endpoints fill only the half pointing into the range;
          // in-range days fill the whole cell. Outer corners round at the range
          // ends and at each week-row / month edge so the band reads as soft pills.
          const col = index % 7;
          const hasRange = Boolean(range && compareDay(range.from, range.to) !== 0);
          const showBand = inRange || (hasRange && selected);
          const bandShape: BandShape = hasRange && selectedStart ? "start" : hasRange && selectedEnd ? "end" : "middle";

          return (
            <CalendarDayCell
              key={day}
              onMouseEnter={() => onHoverDate(date)}
              onMouseLeave={() => onHoverDate(undefined)}
            >
              {showBand && (
                <CalendarRangeBand
                  ownerState={{
                    shape: bandShape,
                    roundLeft: col === 0 || day === 1,
                    roundRight: col === 6 || day === totalDays
                  }}
                  aria-hidden="true"
                />
              )}
              <CalendarDayButton
                type="button"
                disabled={disabled}
                onClick={() => onSelectDate(date)}
                aria-pressed={selected}
                aria-label={defaultFormatDate(date)}
                ownerState={{
                  // Range endpoints square off the side that meets the band so they sit flush
                  // with it; the start keeps its left corners, the end keeps its right corners.
                  shape: hasRange && selectedStart ? "start" : hasRange && selectedEnd ? "end" : "single",
                  selected,
                  inRange,
                  disabled
                }}
              >
                {day}
              </CalendarDayButton>
            </CalendarDayCell>
          );
        })}
      </CalendarDaysGrid>
        </>
      )}
    </CalendarRoot>
  );
}

const YEAR_LAST_30_DAYS = "last30Days";
const YEAR_LAST_ONE = "lastOneYear";
const BASE_YEAR = 2015;

function buildYearOptions() {
  const current = new Date().getFullYear();
  const years: { value: string; label: string }[] = [
    { value: YEAR_LAST_30_DAYS, label: "Last 30 days" },
    { value: YEAR_LAST_ONE, label: "Last one year" }
  ];
  for (let y = current; y >= BASE_YEAR; y--) {
    years.push({ value: String(y), label: String(y) });
  }
  return years;
}

const YEAR_OPTIONS = buildYearOptions();

function computeLastOneYearRange(): { min: Date; max: Date } {
  const today = startOfDay(new Date());
  const minMonthStart = new Date(today.getFullYear() - 1, today.getMonth() - 1, 1);
  const minDay = Math.min(today.getDate(), daysInMonth(minMonthStart.getFullYear(), minMonthStart.getMonth()));

  return {
    min: new Date(minMonthStart.getFullYear(), minMonthStart.getMonth(), minDay),
    max: today
  };
}

function computeYearRange(yearValue: string): { min: Date; max: Date } {
  if (yearValue === YEAR_LAST_30_DAYS) {
    const today = startOfDay(new Date());
    return { min: addDays(today, -29), max: today };
  }
  if (yearValue === YEAR_LAST_ONE) {
    return computeLastOneYearRange();
  }
  const y = Number(yearValue);
  return { min: new Date(y, 0, 1), max: new Date(y, 11, 31) };
}

/* ---------- picker slots ---------- */

const DateRangePickerRoot = styled("div", {
  label: "AgenticDateRangePicker-root"
})({
  display: "inline-flex",
  flexDirection: "column",
  gap: ".375rem"
});

const DateRangePickerLabel = styled("div", {
  label: "AgenticDateRangePicker-label"
})({
  fontSize: "12px",
  fontWeight: 600,
  ...textPrimaryToken
});

interface TriggerOwnerState {
  md: boolean;
  empty: boolean;
  smFilled: boolean;
}

const DateRangePickerTrigger = styled("button", {
  label: "AgenticDateRangePicker-trigger",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: TriggerOwnerState }>(({ ownerState }) => [
  {
    display: "inline-flex",
    width: "100%",
    minWidth: "0px",
    alignItems: "center",
    justifyContent: "space-between",
    gap: ".5rem",
    borderRadius: "var(--bfrs-button-radius)",
    borderWidth: "1px",
    ...borderBorderToken,
    ...bgSurfaceCard,
    fontSize: "12px",
    fontWeight: 400,
    ...textPrimaryToken,
    outline: "2px solid transparent",
    outlineOffset: "2px",
    transitionProperty:
      "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
    transitionTimingFunction: "cubic-bezier(0,0,.2,1)",
    transitionDuration: ".15s",
    "&:hover": bgPrimarySoft,
    "&:active": {
      "--tw-bg-opacity": "1",
      backgroundColor: "hsl(var(--bfrs-color-surface-muted) / var(--tw-bg-opacity, 1))"
    },
    "&:focus-visible": twRing("2px", "hsl(var(--bfrs-color-border-focus) / .25)"),
    "&:disabled": {
      pointerEvents: "none",
      opacity: ".55"
    }
  },
  ownerState.md
    ? { height: "2.25rem", paddingLeft: ".75rem", paddingRight: ".75rem" }
    : { height: "1.75rem", paddingLeft: ".75rem", paddingRight: ".75rem" },
  ownerState.empty ? textMuted : textPrimaryToken,
  ownerState.smFilled && {
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border-focus) / var(--tw-border-opacity, 1))"
  }
]);

const DateRangePickerTriggerIcon = styled(CalendarBlank, {
  label: "AgenticDateRangePicker-triggerIcon"
})({
  flexShrink: 0,
  ...textMuted
});

const DateRangePickerTriggerLabel = styled("span", {
  label: "AgenticDateRangePicker-triggerLabel"
})({
  flex: "1 1 0%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  textAlign: "left"
});

const DateRangePickerCaret = styled(CaretDown, {
  label: "AgenticDateRangePicker-caret",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { open: boolean } }>(({ ownerState }) => [
  {
    flexShrink: 0,
    ...textMuted,
    transitionProperty: "transform",
    transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
    transitionDuration: ".15s"
  },
  ownerState.open && twTransform({ "--tw-rotate": "180deg" })
]);

type ContentWidthMode = "presets" | "one" | "two";

const DateRangePickerContent = styled(RadixPopover.Content, {
  label: "AgenticDateRangePicker-content",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { widthMode: ContentWidthMode } }>(({ ownerState }) => [
  {
    zIndex: 1000,
    display: "flex",
    maxHeight: "min(760px,var(--radix-popover-content-available-height))",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: "var(--bfrs-radius-lg)",
    borderWidth: "1px",
    ...borderBorderToken,
    ...bgSurfaceCard,
    "--tw-shadow": "var(--bfrs-shadow-elevated)",
    "--tw-shadow-colored": "var(--bfrs-shadow-elevated)",
    boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)",
    outline: "2px solid transparent",
    outlineOffset: "2px",
    ...kfContentIn,
    animation: "bfrs-bfrs-content-in var(--bfrs-motion-normal) var(--bfrs-ease-out)"
  },
  ownerState.widthMode === "presets" && { width: "14rem" },
  ownerState.widthMode === "one" && { width: "min(calc(100vw - 1rem),420px)" },
  ownerState.widthMode === "two" && { width: "min(calc(100vw - 1rem),700px)" }
]);

const DateRangePickerScroll = styled("div", {
  label: "AgenticDateRangePicker-scroll",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { customPanel: boolean } }>(({ ownerState }) => [
  {
    display: "flex",
    minHeight: "0px",
    flex: "1 1 0%",
    overflow: "auto",
    "&::-webkit-scrollbar": { display: "none" }
  },
  ownerState.customPanel && {
    flexDirection: "column",
    "@media (min-width:768px)": { flexDirection: "row" }
  }
]);

const DateRangePickerPresetColumn = styled("div", {
  label: "AgenticDateRangePicker-presetColumn",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { customPanel: boolean } }>(({ ownerState }) => [
  {
    display: "flex",
    width: "100%",
    flexShrink: 0,
    flexDirection: "column",
    ...borderBorderToken
  },
  ownerState.customPanel && {
    ...bgSurfaceCard,
    "@media (min-width:768px)": {
      width: "144px",
      borderRightWidth: "1px"
    }
  }
]);

const DateRangePickerPresetList = styled("div", {
  label: "AgenticDateRangePicker-presetList"
})({
  flex: "1 1 0%",
  paddingTop: ".5rem",
  paddingBottom: ".5rem"
});

interface PresetButtonOwnerState {
  md: boolean;
  selected: boolean;
  presetDisabled: boolean;
}

const DateRangePickerPresetButton = styled("button", {
  label: "AgenticDateRangePicker-presetButton",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: PresetButtonOwnerState }>(({ ownerState }) => [
  {
    display: "flex",
    width: "100%",
    cursor: "pointer",
    alignItems: "center",
    justifyContent: "space-between",
    gap: ".75rem",
    textAlign: "left",
    fontSize: "12px",
    outline: "2px solid transparent",
    outlineOffset: "2px",
    transitionProperty:
      "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
    transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
    transitionDuration: ".15s",
    "&:hover": bgPrimarySoft,
    "&:focus-visible": bgPrimarySoft
  },
  ownerState.md
    ? { height: "2.25rem", paddingLeft: ".75rem", paddingRight: ".75rem" }
    : { height: "1.75rem", paddingLeft: ".625rem", paddingRight: ".625rem" },
  ownerState.selected
    ? { ...bgPrimarySoft, fontWeight: 700, ...textPrimaryToken }
    : { fontWeight: 500, ...textSecondaryToken },
  ownerState.presetDisabled && {
    cursor: "not-allowed",
    ...textDisabledToken,
    opacity: ".6",
    "&:hover": { backgroundColor: "transparent" }
  }
]);

const DateRangePickerRangeLimit = styled("div", {
  label: "AgenticDateRangePicker-rangeLimit"
})({
  position: "sticky",
  bottom: 0,
  flexShrink: 0,
  display: "flex",
  gap: ".5rem",
  borderTopWidth: "1px",
  ...borderBorderToken,
  ...bgSurfaceCard,
  paddingLeft: "1rem",
  paddingRight: "1rem",
  paddingTop: ".75rem",
  paddingBottom: ".75rem",
  fontSize: ".75rem",
  lineHeight: "1.25rem",
  ...textMuted
});

const DateRangePickerRangeLimitIcon = styled("span", {
  label: "AgenticDateRangePicker-rangeLimitIcon"
})({
  marginTop: ".125rem",
  display: "flex",
  flexShrink: 0
});

const DateRangePickerCustomArea = styled("div", {
  label: "AgenticDateRangePicker-customArea"
})({
  minWidth: "0px",
  flex: "1 1 0%",
  ...bgSurfaceCard
});

const DateRangePickerYearRow = styled("div", {
  label: "AgenticDateRangePicker-yearRow"
})({
  paddingLeft: "1rem",
  paddingRight: "1rem",
  paddingTop: "1rem"
});

const DateRangePickerYearBanner = styled("div", {
  label: "AgenticDateRangePicker-yearBanner"
})({
  display: "flex",
  alignItems: "center",
  gap: ".75rem",
  borderRadius: "var(--bfrs-radius-lg)",
  paddingLeft: ".75rem",
  paddingRight: ".75rem",
  paddingTop: ".5rem",
  paddingBottom: ".5rem"
});

const DateRangePickerYearText = styled("span", {
  label: "AgenticDateRangePicker-yearText"
})({
  flex: "1 1 0%",
  fontSize: "12.5px",
  fontWeight: 500,
  ...textPrimaryToken
});

/* `&&` reproduces the old tailwind-merge behaviour against the Select
 * trigger's own width rules. */
const DateRangePickerYearSelect = styled(Select, {
  label: "AgenticDateRangePicker-yearSelect"
})({
  "&&": {
    width: "8rem",
    flexShrink: 0
  }
});

const DateRangePickerCalendarGrid = styled("div", {
  label: "AgenticDateRangePicker-calendarGrid",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { two: boolean } }>(({ ownerState }) => [
  {
    display: "grid",
    gap: "1rem",
    overflowX: "auto",
    padding: "1rem"
  },
  ownerState.two
    ? { "@media (min-width:768px)": { gridTemplateColumns: "repeat(2,minmax(0,1fr))" } }
    : { gridTemplateColumns: "repeat(1,minmax(0,1fr))" }
]);

const DateRangePickerFooter = styled("div", {
  label: "AgenticDateRangePicker-footer"
})({
  display: "flex",
  flexShrink: 0,
  alignItems: "stretch",
  borderTopWidth: "1px",
  ...borderBorderToken
});

const DateRangePickerFooterInner = styled("div", {
  label: "AgenticDateRangePicker-footerInner"
})({
  display: "flex",
  flex: "1 1 0%",
  flexDirection: "column",
  gap: ".75rem",
  ...bgSurfaceCard,
  paddingLeft: "1.25rem",
  paddingRight: "1.25rem",
  paddingTop: "1rem",
  paddingBottom: "1rem",
  "@media (min-width:640px)": {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between"
  }
});

const DateRangePickerSelectedBlock = styled("div", {
  label: "AgenticDateRangePicker-selectedBlock"
})({
  display: "flex",
  flexDirection: "column",
  gap: ".125rem"
});

const DateRangePickerSelectedText = styled("div", {
  label: "AgenticDateRangePicker-selectedText"
})({
  fontSize: "13px",
  ...textSecondaryToken
});

const DateRangePickerSelectedLabel = styled("span", {
  label: "AgenticDateRangePicker-selectedLabel"
})({
  fontWeight: 600,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-primary) / var(--tw-text-opacity, 1))"
});

const DateRangePickerSelectedValue = styled("span", {
  label: "AgenticDateRangePicker-selectedValue"
})({
  fontWeight: 500
});

const DateRangePickerSelectedTo = styled("span", {
  label: "AgenticDateRangePicker-selectedTo"
})(textMuted);

const DateRangePickerIncomplete = styled("div", {
  label: "AgenticDateRangePicker-incomplete"
})({
  fontSize: "12px",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-danger) / var(--tw-text-opacity, 1))"
});

const DateRangePickerFooterActions = styled("div", {
  label: "AgenticDateRangePicker-footerActions"
})({
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "flex-end",
  gap: ".5rem"
});

const DateRangePickerHelper = styled("div", {
  label: "AgenticDateRangePicker-helper"
})({
  fontSize: ".75rem",
  lineHeight: "1.25rem",
  ...textMuted
});

export function DateRangePicker({
  className,
  value,
  defaultValue,
  onChange,
  presets = defaultDateRangePresets,
  placeholder = "Select date range",
  label,
  helperText,
  size = "md",
  disabled,
  clearable,
  minDate,
  maxDate,
  maxRangeDays,
  months = 2,
  align = "start",
  side = "bottom",
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
  infoText,
  customPresetLabel = "Custom",
  applyLabel = "Apply",
  cancelLabel = "Cancel",
  clearLabel = "Clear",
  formatDate = defaultFormatDate,
  formatRange,
  triggerClassName,
  contentClassName,
  calendarClassName,
  rangeLimitClassName,
  restrictToLast3Months = false,
  singleDateMode = false,
  allowedFutureDate = false,
  showLastOneYearOnly = false,
  ...props
}: DateRangePickerProps) {
  const effectiveMonths: 1 | 2 = singleDateMode ? 1 : months;

  const [uncontrolledValue, setUncontrolledValue] = useState<DateRangeValue>(() => coerceDateRangeValue(defaultValue ?? {}));
  // Coerce the controlled value too: consumers often pass from/to as ISO strings
  // or timestamps (hydrated from API/storage). Normalizing to Date objects keeps
  // the range recognized as "filled" (so the sm trigger border highlights) and
  // avoids crashing the calendar math on non-Date input.
  const committedValue = useMemo(() => coerceDateRangeValue(value ?? uncontrolledValue), [value, uncontrolledValue]);
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const open = controlledOpen ?? uncontrolledOpen;
  const [activePreset, setActivePreset] = useState<DateRangePresetId>(committedValue.preset ?? CUSTOM_PRESET_ID);
  const [draftValue, setDraftValue] = useState<DateRangeValue>(committedValue);
  const [hoveredDate, setHoveredDate] = useState<Date | undefined>();
  const [awaitingTo, setAwaitingTo] = useState(false);
  const [selectedYear, setSelectedYear] = useState<string>(showLastOneYearOnly ? YEAR_LAST_ONE : YEAR_LAST_30_DAYS);
  const [leftCalendar, setLeftCalendar] = useState(() => getInitialCalendar(committedValue, effectiveMonths));
  const [rightCalendar, setRightCalendar] = useState(() => {
    const left = getInitialCalendar(committedValue, effectiveMonths);
    return shiftMonth(left.year, left.month, 1);
  });
  const visibleCalendarMonths = useMemo(
    () => getVisibleCalendarMonthCount(effectiveMonths, draftValue),
    [draftValue, effectiveMonths]
  );

  const yearRange = useMemo(() => {
    if (restrictToLast3Months) {
      const today = startOfDay(new Date());
      return { min: addDays(today, -90), max: today };
    }
    return computeYearRange(selectedYear);
  }, [restrictToLast3Months, selectedYear]);

  const effectiveMinDate = useMemo(() => {
    if (allowedFutureDate) {
      return addDays(startOfDay(new Date()), 1);
    }
    const base = yearRange.min;
    // If a minDate prop is passed, use whichever is more restrictive (later)
    if (minDate && compareDay(startOfDay(minDate), base) > 0) {
      return startOfDay(minDate);
    }
    return base;
  }, [allowedFutureDate, yearRange, minDate]);

  const effectiveMaxDate = useMemo(() => {
    if (allowedFutureDate) {
      return addDays(startOfDay(new Date()), 365);
    }
    const base = yearRange.max;
    // If a maxDate prop is passed, use whichever is more restrictive (earlier)
    if (maxDate && compareDay(startOfDay(maxDate), base) < 0) {
      return startOfDay(maxDate);
    }
    return base;
  }, [allowedFutureDate, yearRange, maxDate]);

  const effectiveMinMonth = useMemo<CalendarMonthCoordinate>(
    () => ({ year: effectiveMinDate.getFullYear(), month: effectiveMinDate.getMonth() }),
    [effectiveMinDate]
  );

  const effectiveMaxMonth = useMemo<CalendarMonthCoordinate>(
    () => ({ year: effectiveMaxDate.getFullYear(), month: effectiveMaxDate.getMonth() }),
    [effectiveMaxDate]
  );

  const leftCalendarMaxMonth = useMemo(() => {
    if (visibleCalendarMonths !== 2) return effectiveMaxMonth;
    return shiftMonth(rightCalendar.year, rightCalendar.month, -1);
  }, [effectiveMaxMonth, rightCalendar, visibleCalendarMonths]);

  const rightCalendarMinMonth = useMemo(() => shiftMonth(leftCalendar.year, leftCalendar.month, 1), [leftCalendar]);

  const setOpen = useCallback(
    (nextOpen: boolean) => {
      if (controlledOpen === undefined) {
        setUncontrolledOpen(nextOpen);
      }
      onOpenChange?.(nextOpen);
    },
    [controlledOpen, onOpenChange]
  );

  const commitValue = useCallback(
    (nextValue: DateRangeValue) => {
      if (value === undefined) {
        setUncontrolledValue(nextValue);
      }
      onChange?.(nextValue);
    },
    [onChange, value]
  );

  useEffect(() => {
    if (!open) return;
    setDraftValue(committedValue);
    setActivePreset(committedValue.preset ?? (hasCompleteRange(committedValue) ? CUSTOM_PRESET_ID : ""));
    setHoveredDate(undefined);
    setAwaitingTo(false);
    if (hasCompleteRange(committedValue) && effectiveMonths === 2) {
      const window = getRangeCalendarWindow(committedValue.from, committedValue.to, effectiveMonths);
      setLeftCalendar(window.left);
      setRightCalendar(window.right);
    } else {
      const left = getInitialCalendar(committedValue, effectiveMonths);
      setLeftCalendar(left);
      setRightCalendar(shiftMonth(left.year, left.month, 1));
    }

    // Sync year picker only for custom ranges committed in a past year
    // (matches Angular's prefillRange which only fires for year <= last year, not current year)
    const currentYear = new Date().getFullYear();
    if (hasCompleteRange(committedValue) && committedValue.preset === CUSTOM_PRESET_ID) {
      const fromYear = committedValue.from.getFullYear();
      const toYear = committedValue.to.getFullYear();
      if (fromYear === toYear && fromYear >= BASE_YEAR && fromYear < currentYear) {
        setSelectedYear(String(fromYear));
      } else {
        // Committed custom range in the current year (or spanning years): keep
        // the year-wide window so the committed range stays selectable.
        setSelectedYear(YEAR_LAST_ONE);
      }
    } else {
      // No committed custom range — default window is the last 30 days,
      // matching the draft that entering custom mode pre-selects (unless the
      // picker is pinned to the last-one-year option).
      setSelectedYear(showLastOneYearOnly ? YEAR_LAST_ONE : YEAR_LAST_30_DAYS);
    }
  }, [committedValue, effectiveMonths, open, showLastOneYearOnly]);

  const triggerLabel = useMemo(() => {
    if (formatRange) {
      return formatRange(committedValue);
    }

    const presetLabel = getPresetLabel(presets, committedValue.preset);
    if (presetLabel && committedValue.preset !== CUSTOM_PRESET_ID) {
      return presetLabel;
    }

    if (hasCompleteRange(committedValue)) {
      return sameDay(committedValue.from, committedValue.to)
        ? formatDate(committedValue.from)
        : `${formatDate(committedValue.from)} - ${formatDate(committedValue.to)}`;
    }

    return placeholder;
  }, [committedValue, formatDate, formatRange, placeholder, presets]);

  const handleYearChange = useCallback(
    (yearValue: string) => {
      setSelectedYear(yearValue);
      const range = computeYearRange(yearValue);
      setDraftValue({ preset: CUSTOM_PRESET_ID, from: range.min, to: range.max });
      const window = getRangeCalendarWindow(range.min, range.max, effectiveMonths);
      setLeftCalendar(window.left);
      setRightCalendar(window.right);
      setAwaitingTo(false);
      setHoveredDate(undefined);
    },
    [effectiveMonths]
  );

  const isDateDisabled = useCallback(
    (date: Date) => {
      const normalizedDate = startOfDay(date);
      const normalizedMin = startOfDay(effectiveMinDate);
      const normalizedMax = startOfDay(effectiveMaxDate);

      if (compareDay(normalizedDate, normalizedMin) < 0) return true;
      if (compareDay(normalizedDate, normalizedMax) > 0) return true;
      if (awaitingTo && draftValue.from && maxRangeDays && differenceInCalendarDays(normalizedDate, draftValue.from) + 1 > maxRangeDays) return true;

      return false;
    },
    [awaitingTo, draftValue.from, effectiveMinDate, effectiveMaxDate, maxRangeDays]
  );

  const selectPreset = (preset: DateRangePreset) => {
    if (preset.disabled) return;

    const presetRange = preset.getRange();
    const range = normalizeRange(presetRange.from, presetRange.to);
    commitValue({ preset: preset.id, ...range });
    setOpen(false);
  };

  const startCustomSelection = () => {
    setActivePreset(CUSTOM_PRESET_ID);
    // Default the custom draft to the last 30 days, clamped to the allowed
    // window and maxRangeDays. Picking a year from the dropdown still applies
    // that full year via handleYearChange.
    const clampDay = (date: Date) =>
      compareDay(date, effectiveMinDate) < 0 ? effectiveMinDate : compareDay(date, effectiveMaxDate) > 0 ? effectiveMaxDate : date;
    const to = clampDay(startOfDay(new Date()));
    const spanDays = maxRangeDays ? Math.min(30, maxRangeDays) : 30;
    const from = clampDay(addDays(to, -(spanDays - 1)));
    setDraftValue({ preset: CUSTOM_PRESET_ID, from, to });
    const window = getRangeCalendarWindow(from, to, effectiveMonths);
    setLeftCalendar(window.left);
    setRightCalendar(window.right);
    setAwaitingTo(false);
    setHoveredDate(undefined);
  };

  const selectDate = (date: Date) => {
    if (isDateDisabled(date)) return;

    const selected = startOfDay(date);

    if (singleDateMode) {
      setDraftValue({ preset: CUSTOM_PRESET_ID, from: selected, to: selected });
      setActivePreset(CUSTOM_PRESET_ID);
      return;
    }

    if (!awaitingTo || !draftValue.from || draftValue.to) {
      setDraftValue({ preset: CUSTOM_PRESET_ID, from: selected, to: undefined });
      setActivePreset(CUSTOM_PRESET_ID);
      setAwaitingTo(true);
      return;
    }

    const range = normalizeRange(draftValue.from, selected);
    setDraftValue({ preset: CUSTOM_PRESET_ID, ...range });
    setAwaitingTo(false);
  };

  const applyCustomRange = () => {
    if (!hasCompleteRange(draftValue)) return;

    commitValue({ preset: CUSTOM_PRESET_ID, from: draftValue.from, to: draftValue.to });
    setOpen(false);
  };

  const cancelCustomRange = () => {
    setDraftValue(committedValue);
    setActivePreset(committedValue.preset ?? (hasCompleteRange(committedValue) ? CUSTOM_PRESET_ID : ""));
    setAwaitingTo(false);
    setHoveredDate(undefined);
    setOpen(false);
  };

  const clearValue = () => {
    commitValue({});
    setDraftValue({});
    setActivePreset("");
    setAwaitingTo(false);
    setOpen(false);
  };

  const hasDraftRange = hasCompleteRange(draftValue);
  const showCustomPanel = activePreset === CUSTOM_PRESET_ID;
  const canApply = singleDateMode ? Boolean(draftValue.from) : hasDraftRange;

  const defaultRangeLimit = restrictToLast3Months
    ? "Date can be viewed only for the last 90 days."
    : singleDateMode && allowedFutureDate
      ? "Date can be viewed only for a period of future date."
      : "Date can be viewed only for a period of up to 1 year.";
  const showRangeLimit = infoText ?? (maxRangeDays ? `Date range can be selected for up to ${maxRangeDays} days.` : defaultRangeLimit);

  // Boundary checks for calendar navigation
  const canGoPrev = compareMonth(leftCalendar, effectiveMinMonth) > 0;
  const canGoNextVisible = visibleCalendarMonths === 2
    ? compareMonth(rightCalendar, effectiveMaxMonth) < 0
    : compareMonth(leftCalendar, effectiveMaxMonth) < 0;

  return (
    <DateRangePickerRoot className={className} {...props}>
      {label && <DateRangePickerLabel>{label}</DateRangePickerLabel>}
      <RadixPopover.Root open={open} onOpenChange={setOpen}>
        <RadixPopover.Trigger asChild>
          <DateRangePickerTrigger
            type="button"
            disabled={disabled}
            data-filled={hasCompleteRange(committedValue) || committedValue.preset ? "" : undefined}
            ownerState={{
              md: size === "md",
              empty: !hasCompleteRange(committedValue) && !committedValue.preset,
              smFilled: size === "sm" && (hasCompleteRange(committedValue) || Boolean(committedValue.preset))
            }}
            className={triggerClassName}
          >
            <DateRangePickerTriggerIcon size={13} />
            <DateRangePickerTriggerLabel>{triggerLabel}</DateRangePickerTriggerLabel>
            <DateRangePickerCaret size={10} weight="bold" ownerState={{ open }} />
          </DateRangePickerTrigger>
        </RadixPopover.Trigger>
        <RadixPopover.Portal>
          <BfrsPortalScope>
            <DateRangePickerContent
              align={align}
              side={side}
              sideOffset={6}
              collisionPadding={floatingCollisionPadding}
              ownerState={{ widthMode: showCustomPanel ? (visibleCalendarMonths === 1 ? "one" : "two") : "presets" }}
              className={contentClassName}
            >
              <DateRangePickerScroll ownerState={{ customPanel: showCustomPanel }}>
                <DateRangePickerPresetColumn ownerState={{ customPanel: showCustomPanel }}>
                  <DateRangePickerPresetList>
                    {presets.map((preset) => {
                      const selected = activePreset === preset.id;

                      return (
                        <DateRangePickerPresetButton
                          key={preset.id}
                          type="button"
                          disabled={preset.disabled}
                          onClick={() => selectPreset(preset)}
                          ownerState={{ md: size === "md", selected, presetDisabled: Boolean(preset.disabled) }}
                        >
                          <span>{preset.label}</span>
                          {selected && <Check size={14} weight="bold" />}
                        </DateRangePickerPresetButton>
                      );
                    })}
                    <DateRangePickerPresetButton
                      type="button"
                      onClick={startCustomSelection}
                      ownerState={{ md: size === "md", selected: showCustomPanel, presetDisabled: false }}
                    >
                      <span>{customPresetLabel}</span>
                      {showCustomPanel && <Check size={14} weight="bold" />}
                    </DateRangePickerPresetButton>
                  </DateRangePickerPresetList>
                  {showRangeLimit && showCustomPanel && (
                    <DateRangePickerRangeLimit className={rangeLimitClassName}>
                      <DateRangePickerRangeLimitIcon>
                        <WarningAlertIcon />
                      </DateRangePickerRangeLimitIcon>
                      <span>{showRangeLimit}</span>
                    </DateRangePickerRangeLimit>
                  )}
                </DateRangePickerPresetColumn>

                {showCustomPanel && (
                  <DateRangePickerCustomArea>
                    {!restrictToLast3Months && !allowedFutureDate && !singleDateMode && (
                      <DateRangePickerYearRow>
                        <DateRangePickerYearBanner style={warningSurfaceTextStyle}>
                          <DateRangePickerYearText>
                            Please select the year you want to view data for
                          </DateRangePickerYearText>
                          <DateRangePickerYearSelect
                            size="sm"
                            value={selectedYear}
                            onValueChange={handleYearChange}
                            options={showLastOneYearOnly ? YEAR_OPTIONS.filter((option) => option.value === YEAR_LAST_ONE) : YEAR_OPTIONS}
                          />
                        </DateRangePickerYearBanner>
                      </DateRangePickerYearRow>
                    )}
                    <DateRangePickerCalendarGrid ownerState={{ two: visibleCalendarMonths === 2 }}>
                      <CalendarMonth
                        year={leftCalendar.year}
                        month={leftCalendar.month}
                        from={draftValue.from}
                        to={draftValue.to}
                        hovered={hoveredDate}
                        awaitingTo={awaitingTo}
                        isDateDisabled={isDateDisabled}
                        onSelectDate={selectDate}
                        onHoverDate={setHoveredDate}
                        onPrev={() => setLeftCalendar((current) => shiftMonth(current.year, current.month, -1))}
                        onNext={() => setLeftCalendar((current) => shiftMonth(current.year, current.month, 1))}
                        onNavigateTo={(y, m) => setLeftCalendar({ year: y, month: m })}
                        minMonth={effectiveMinMonth}
                        maxMonth={leftCalendarMaxMonth}
                        showPrev={canGoPrev}
                        showNext={visibleCalendarMonths !== 2 && canGoNextVisible}
                        className={calendarClassName}
                      />
                      {visibleCalendarMonths === 2 && (
                        <CalendarMonth
                          year={rightCalendar.year}
                          month={rightCalendar.month}
                          from={draftValue.from}
                          to={draftValue.to}
                          hovered={hoveredDate}
                          awaitingTo={awaitingTo}
                          isDateDisabled={isDateDisabled}
                          onSelectDate={selectDate}
                          onHoverDate={setHoveredDate}
                          onPrev={() => setRightCalendar((current) => shiftMonth(current.year, current.month, -1))}
                          onNext={() => setRightCalendar((current) => shiftMonth(current.year, current.month, 1))}
                          onNavigateTo={(y, m) => setRightCalendar({ year: y, month: m })}
                          minMonth={rightCalendarMinMonth}
                          maxMonth={effectiveMaxMonth}
                          showPrev={false}
                          showNext={canGoNextVisible}
                          className={calendarClassName}
                        />
                      )}
                    </DateRangePickerCalendarGrid>
                  </DateRangePickerCustomArea>
                )}
              </DateRangePickerScroll>
              {showCustomPanel && (
                <DateRangePickerFooter>
                  <DateRangePickerFooterInner>
                    <DateRangePickerSelectedBlock>
                      <DateRangePickerSelectedText>
                        <DateRangePickerSelectedLabel>Selected:</DateRangePickerSelectedLabel>{" "}
                        <DateRangePickerSelectedValue>{draftValue.from ? formatDate(draftValue.from) : "--/--/----"}</DateRangePickerSelectedValue>
                        {!singleDateMode && (
                          <>
                            {" "}<DateRangePickerSelectedTo>To</DateRangePickerSelectedTo>{" "}
                            <DateRangePickerSelectedValue>{draftValue.to ? formatDate(draftValue.to) : "--/--/----"}</DateRangePickerSelectedValue>
                          </>
                        )}
                      </DateRangePickerSelectedText>
                      {awaitingTo && !singleDateMode && (
                        <DateRangePickerIncomplete>
                          Date range incomplete. Select both Start and End dates.
                        </DateRangePickerIncomplete>
                      )}
                    </DateRangePickerSelectedBlock>
                    <DateRangePickerFooterActions>
                      {clearable && (
                        <Button variant="ghost" size="sm" leftIcon={<X size={13} weight="bold" />} onClick={clearValue}>
                          {clearLabel}
                        </Button>
                      )}
                      <Button variant="outline" size="sm" onClick={cancelCustomRange}>
                        {cancelLabel}
                      </Button>
                      <Button size="sm" disabled={!canApply} onClick={applyCustomRange}>
                        {applyLabel}
                      </Button>
                    </DateRangePickerFooterActions>
                  </DateRangePickerFooterInner>
                </DateRangePickerFooter>
              )}
            </DateRangePickerContent>
          </BfrsPortalScope>
        </RadixPopover.Portal>
      </RadixPopover.Root>
      {helperText && <DateRangePickerHelper>{helperText}</DateRangePickerHelper>}
    </DateRangePickerRoot>
  );
}
